import type { IBasePageAnalyticMetric } from '~/modules/page-analytic/types';
import type { TrendIndicatorProps } from '@shopify/polaris-viz';
import type { IBasePathAnalyticPageWithChild } from '../types';

import {
  getTotalSessionCompletedCheckoutMetric,
  getTotalSessionReachedCheckoutMetric,
  getTotalSessionWithCartAdditionMetric,
} from '~/modules/analytics/helpers/metric';
import { formatPercentage } from '~/utils';
export const calculateFunnelTrendChange = ({
  currentValue,
  previousValue,
}: {
  currentValue?: number;
  previousValue?: number;
}): TrendIndicatorProps => {
  const shouldSkipComparison =
    typeof currentValue !== 'number' || typeof previousValue !== 'number' || previousValue === 0;

  if (shouldSkipComparison) return {};

  const changePercentage = ((currentValue - previousValue) / previousValue) * 100;
  const formattedChange = formatPercentage(Math.abs(changePercentage));
  const trend = changePercentage > 0 ? 'positive' : changePercentage < 0 ? 'negative' : 'neutral';
  const direction = changePercentage > 0 ? 'upward' : changePercentage < 0 ? 'downward' : undefined;

  return { value: formattedChange, trend, direction };
};

export const calculateFirstLevelTrend = ({
  pageWithMetric,
  previousPathAnalyticsMap,
}: {
  pageWithMetric?: IBasePathAnalyticPageWithChild;
  previousPathAnalyticsMap: Record<string, IBasePathAnalyticPageWithChild>;
}) => {
  if (!pageWithMetric) return null;

  const currentValue = pageWithMetric?.metric?.totalSessions;
  const previousValue = previousPathAnalyticsMap[`${pageWithMetric?.id}`]?.metric?.totalSessions;

  return calculateFunnelTrendChange({ currentValue, previousValue });
};

export const calculateDynamicLevelsTrends = (
  dynamicLevels: IBasePathAnalyticPageWithChild[],
  previousPathAnalyticsMap: Record<string, IBasePathAnalyticPageWithChild>,
) => {
  const trends: Record<string, TrendIndicatorProps> = {};

  dynamicLevels.forEach((level, index) => {
    const levelPreviousData = previousPathAnalyticsMap[level.id];

    const levelTrend = calculateFunnelTrendChange({
      currentValue: level.metric?.totalSessions,
      previousValue: levelPreviousData?.metric?.totalSessions,
    });
    trends[`${index + 1}`] = levelTrend;
  });

  return trends;
};

export const calculateConversionMetricsTrends = (
  dynamicLevels: IBasePathAnalyticPageWithChild[],
  previousPathAnalyticsMap: Record<string, IBasePathAnalyticPageWithChild>,
  pageWithMetric?: IBasePathAnalyticPageWithChild,
) => {
  const lastLevel = dynamicLevels[dynamicLevels.length - 1] || pageWithMetric;
  const lastLevelPreviousData = previousPathAnalyticsMap[lastLevel.id];

  const metric = lastLevel?.metric as IBasePageAnalyticMetric;
  const previousMetric = lastLevelPreviousData?.metric as IBasePageAnalyticMetric;
  const totalAddedToCart = getTotalSessionWithCartAdditionMetric({ metric }).value ?? 0;
  const totalReachedCheckout = getTotalSessionReachedCheckoutMetric({ metric }).value ?? 0;
  const totalCompletedCheckout = getTotalSessionCompletedCheckoutMetric({ metric }).value ?? 0;

  const totalAddedToCartPrevious =
    getTotalSessionWithCartAdditionMetric({ metric: previousMetric }).value ?? 0;
  const totalReachedCheckoutPrevious =
    getTotalSessionReachedCheckoutMetric({ metric: previousMetric }).value ?? 0;
  const totalCompletedCheckoutPrevious =
    getTotalSessionCompletedCheckoutMetric({ metric: previousMetric }).value ?? 0;

  const trends: Record<string, TrendIndicatorProps> = {};
  const conversionLevelIndex = dynamicLevels.length + 1;

  const cartTrend = calculateFunnelTrendChange({
    currentValue: Number(totalAddedToCart ?? 0),
    previousValue: Number(totalAddedToCartPrevious ?? 0),
  });
  const checkoutTrend = calculateFunnelTrendChange({
    currentValue: Number(totalReachedCheckout ?? 0),
    previousValue: Number(totalReachedCheckoutPrevious ?? 0),
  });
  const completedTrend = calculateFunnelTrendChange({
    currentValue: Number(totalCompletedCheckout ?? 0),
    previousValue: Number(totalCompletedCheckoutPrevious ?? 0),
  });

  trends[`${conversionLevelIndex}`] = cartTrend;
  trends[`${conversionLevelIndex + 1}`] = checkoutTrend;
  trends[`${conversionLevelIndex + 2}`] = completedTrend;

  return trends;
};

export const calculateConversionFunnelTrends = ({
  pageWithMetric,
  previousPathAnalyticsMap,
  dynamicLevels,
}: {
  pageWithMetric?: IBasePathAnalyticPageWithChild;
  previousPathAnalyticsMap: Record<string, IBasePathAnalyticPageWithChild>;
  dynamicLevels: IBasePathAnalyticPageWithChild[];
}) => {
  const firstLevelTrend = calculateFirstLevelTrend({
    pageWithMetric,
    previousPathAnalyticsMap,
  });
  if (!firstLevelTrend) return {};

  const trends: Record<string, TrendIndicatorProps> = {
    '0': firstLevelTrend,
    ...calculateDynamicLevelsTrends(dynamicLevels, previousPathAnalyticsMap),
    ...calculateConversionMetricsTrends(dynamicLevels, previousPathAnalyticsMap, pageWithMetric),
  };

  return trends;
};
